import React, { useState, useRef, useEffect } from 'react';

import { Menu, Bot, MessageSquare, Search as SearchIcon, User, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { SIDEBAR_W, SIDEBAR_COLLAPSED_W } from '@/components/v2/defaults';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from '@/components/shared/NotificationBell';
import ThemeToggle from '@/components/ui/ThemeToggle';

// Generate initials from name (same logic as SidebarProfileV2)
const getInitials = (name: string): string => {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
};

const TopBarV2: React.FC<{
  onBurger: () => void;
  onSearch?: (q: string) => void;
  isSidebarCollapsed?: boolean;
}> = ({ onBurger, onSearch, isSidebarCollapsed = false }) => {
  const [q, setQ] = useState('');
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const userName = user?.name || 'User';
  const userEmail = user?.email || '';
  const initials = getInitials(userName);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        profileButtonRef.current &&
        !profileButtonRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [profileMenuOpen]);

  const handleProfileClick = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const handleGoToProfile = () => {
    setProfileMenuOpen(false);
    navigate('/settings');
  };

  const handleSignOut = async () => {
    setProfileMenuOpen(false);
    try {
      await logout();
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  return (
    <header
      id="app-topbar"
      className="fixed top-0 z-40 border-b border-slate-200 dark:border-neutral-700 bg-white/95 dark:bg-neutral-900/95 backdrop-blur"
    >
      <div className="flex h-16 w-full items-center gap-3 px-4">
        <button className="md:hidden" onClick={onBurger} aria-label="Open sidebar">
          <Menu className="h-5 w-5" />
        </button>
        <div
          className="pl-1 sm:pl-2 lg:pl-4 page-title logoText text-neutral-900 dark:text-neutral-100"
          style={{ fontSize: 27 }}
        >
          BassitBros AI
        </div>
        <div className="relative ml-1 flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 dark:text-neutral-400" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') onSearch?.(q);
            }}
            placeholder="Search everything—cases, clients, docs"
            aria-label="Global search"
            className="h-10 md:h-11 w-full rounded-xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-8 text-sm outline-none placeholder:text-slate-500 dark:placeholder:text-neutral-400 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-amber-100 dark:focus:ring-amber-900/50"
          />
        </div>
        <div className="hidden items-center gap-2 md:flex pr-1 sm:pr-2 lg:pr-3">
          <ThemeToggle size="sm" />
          <button className="inline-flex items-center gap-1 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm">
            <Bot className="h-3.5 w-3.5" /> Agentic AI
          </button>
          <button
            className="rounded-2xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2.5 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
            aria-label="Messages"
          >
            <MessageSquare className="h-4 w-4" />
          </button>
          <span>
            <NotificationBell />
          </span>

          {/* Profile Button with Dropdown */}
          <div className="relative">
            <button
              ref={profileButtonRef}
              onClick={handleProfileClick}
              className="rounded-2xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2.5 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-neutral-700 transition-colors"
              aria-label="Profile menu"
            >
              <div className="grid h-5 w-5 place-items-center rounded-full bg-gradient-to-br from-amber-400 to-orange-600 text-[10px] font-semibold text-white">
                {initials}
              </div>
            </button>

            {/* Dropdown Menu */}
            {profileMenuOpen && (
              <div
                ref={menuRef}
                className="absolute right-0 top-full mt-2 w-64 rounded-2xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg z-50"
              >
                {/* User Info Section */}
                <div className="p-4 border-b border-slate-200 dark:border-neutral-700">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 p-[2px]">
                      <div className="grid h-full w-full place-items-center rounded-full bg-white dark:bg-neutral-800 text-sm font-semibold text-amber-600 dark:text-amber-400">
                        {initials}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className="truncate text-sm font-semibold text-slate-900 dark:text-neutral-100"
                        title={userName}
                      >
                        {userName}
                      </div>
                      {userEmail && (
                        <div
                          className="truncate text-xs text-slate-600 dark:text-neutral-400"
                          title={userEmail}
                        >
                          {userEmail}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="p-2">
                  <button
                    onClick={handleGoToProfile}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-slate-700 dark:text-neutral-300 hover:bg-slate-50 dark:hover:bg-neutral-700 transition-colors"
                  >
                    <User className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
                    <span>Profile & Settings</span>
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .text-brand-600{color:#ea580c}
        .logoText{font-family: Georgia, "Times New Roman", serif; font-weight:600;}
        #app-topbar{ left:0; width:100%; transition: left 0.3s ease-in-out, width 0.3s ease-in-out; }
        @media (min-width:768px){
          #app-topbar{ left:${isSidebarCollapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W}px; width:calc(100% - ${isSidebarCollapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W}px); }
        }
      `}</style>
    </header>
  );
};

export default TopBarV2;
